<template>
  <div class="practice-detail-container">
    <div class="practice-detail">
      <div class="exam-header">
        <div class="logo">
          <img src="@/assets/new_picture/LOGO_b.png" alt="Logo" class="logo-img">
        </div>
        <div class="exam-title">{{ examTitle }}</div>
        <div class="exit-exam" @click="exitPractice">退出练习</div>
      </div>
      
      <div class="exam-nav">
        <el-breadcrumb separator="/"></el-breadcrumb>
      </div>
      
      <div class="exam-content">
        <div class="left-panel">
          <div class="answer-card">
            <div class="card-title">答题卡</div>
            <div class="question-numbers">
              <div 
                v-for="(q, index) in questions" 
                :key="q.id" 
                class="question-number"
                :class="getQuestionStatus(q.id)"
                @click="scrollToQuestion(index)"
              >
                {{ index + 1 }}
              </div>
            </div>
            <div class="divider"></div>

            <div class="card-footer">
              <div class="status-info">
                <div class="status-item">
                  <div class="status-box answered"></div>
                  <span>已答</span>
                </div>
                <div class="status-item">
                  <div class="status-box unanswered"></div>
                  <span>未答</span>
                </div>
              </div>
              <el-button type="primary" class="submit-btn" @click="submitAnswers">提交并查看结果</el-button>
            </div>
          </div>
        </div>
        
        <div class="right-panel">
          <div v-for="(question, index) in questions" :key="question.id" class="question-item" :ref="'question_' + index">
            <div class="question-header">
              <div class="question-number">{{ index + 1 }}.</div>
              <div class="question-title">
                {{ question.title }}
                <span class="question-type">({{ question.type === 'single' ? '单选' : (question.type === 'multiple' ? '多选' : '问答题') }})</span>
              </div>
            </div>
            <div class="question-options" v-if="question.type !== 'shortAnswer'">
              <div 
                v-for="option in question.options" 
                :key="option.id" 
                class="option-item"
                :class="{
                  'selected': question.type === 'single' ? answers[question.id] === option.id : answers[question.id] && answers[question.id].includes(option.id),
                  'disabled': (question.type === 'single' && answers[question.id] && answers[question.id] !== option.id) || (question.type === 'multiple' && confirmedQuestions.includes(question.id))
                }"
              >
                <button 
                  type="button"
                  class="option-circle"
                  @click="selectOption(question.id, option.id, question.type)"
                  :disabled="(question.type === 'single' && !!answers[question.id] && answers[question.id] !== option.id) || (question.type === 'multiple' && confirmedQuestions.includes(question.id))"
                >
                  {{ option.letter }}
                </button>
                <div class="option-text">{{ option.content }}</div>
              </div>
            </div>
            <div class="confirm-option" v-if="question.type === 'multiple' || question.type === 'shortAnswer'">
              <div v-if="question.type === 'shortAnswer'" class="short-answer">
                <el-input
                  type="textarea"
                  :rows="6"
                  v-model="answers[question.id]"
                  :disabled="confirmedQuestions.includes(question.id)"
                  placeholder=""
                />
              </div>
              <el-button 
                type="primary" 
                size="small" 
                @click="question.type === 'shortAnswer' ? confirmShortAnswer(question.id) : confirmOption(question.id)"
                :disabled="confirmedQuestions.includes(question.id) || (question.type === 'multiple' ? !(Array.isArray(answers[question.id]) && answers[question.id].length > 0) : !(answers[question.id] && String(answers[question.id]).trim().length > 0))"
              >
                {{ question.type === 'shortAnswer' ? '确认提交' : '确认选项' }}
              </el-button>
            </div>
            
            <!-- 解析部分 -->
            <div v-if="question.type === 'single' ? answers[question.id] : confirmedQuestions.includes(question.id)" class="question-analysis" :class="isAnswerCorrect(question.id) ? 'correct' : 'wrong'">
              <div class="analysis-header">
                <i :class="['analysis-icon', isAnswerCorrect(question.id) ? 'correct' : 'wrong', isAnswerCorrect(question.id) ? 'el-icon-circle-check' : 'el-icon-close']"></i>
                <div class="analysis-title" :class="isAnswerCorrect(question.id) ? 'correct' : 'wrong'">
                  {{ isAnswerCorrect(question.id) ? '回答正确' : '回答错误' }}
                </div>
              </div>
              <div class="analysis-row" v-if="question.type !== 'shortAnswer'">
                <span class="label">正确答案：</span>
                <span class="value correct">{{ getCorrectAnswerLetters(question) }}</span>
              </div>
              <div class="analysis-row" v-if="question.type !== 'shortAnswer' && getUserAnswerLetters(question.id)">
                <span class="label">您的答案：</span>
                <span class="value user">{{ getUserAnswerLetters(question.id) }}</span>
              </div>
              <div class="analysis-row" v-if="question.type === 'shortAnswer' && question.keywords && question.keywords.length">
                <span class="label">参考关键词：</span>
                <span class="value keywords">{{ question.keywords.join('、') }}</span>
              </div>
              <div class="analysis-row" v-if="question.type === 'shortAnswer' && answers[question.id]">
                <span class="label">您的答案：</span>
                <span class="value user">{{ answers[question.id] }}</span>
              </div>
              <div class="analysis-row">
                <span class="label">答案解析：</span>
                <span class="value">{{ question.analysis }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      

    </div>
    
    <!-- 结果弹窗 -->
    <el-dialog
      title="考试结果"
      :visible.sync="showResultDialog"
      width="500px"
      center
      :show-close="false"
      :close-on-click-modal="false"
    >
      <div class="result-content">
        <div class="result-score">
          <div class="score-circle">
            <div class="score-value">{{ calculateCorrectRate() }}%</div>
          </div>
        </div>
        <div class="result-stats">
          <div class="stat-item">
            <span class="stat-label">题目数：</span>
            <span class="stat-value">{{ questions.length }}题</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">正确数：</span>
            <span class="stat-value">{{ questions.length - calculateWrongCount() - calculateUnansweredCount() }}题</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">错误数：</span>
            <span class="stat-value">{{ calculateWrongCount() }}题</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">未答数：</span>
            <span class="stat-value">{{ calculateUnansweredCount() }}题</span>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="retryPractice">再次答题</el-button>
        <el-button @click="backToMyCourses">{{ backButtonText }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getPaperPractice, getPaperQuestion, submitFrontendExam, submitWrongQuestionPractice } from '@/api/examApi'

export default {
  name: 'PracticeDetail',
  data() {
    return {
      examTitle: '',
      paperId: null,
      paperSessionId: null,
      paperType: null,
      startTime: 0,
      totalScore: 0,
      passScore: 0,
      answers: {}, // 存储用户答案
      showResultDialog: false, // 控制结果弹窗显示
      confirmedQuestions: [], // 已确认的题目ID
      questions: [],
      questionDetailCache: {},
      isWrongPractice: false
    }
  },
  created() {
    this.paperId = this.$route.params.id
    this.isWrongPractice = this.$route.query.source === 'wrong_practice'
    if (this.paperId) {
      this.fetchPaper()
    }
  },
  computed: {
    backButtonText() {
      return this.$route.query.source === 'wrong_practice' ? '返回错题记录' : '返回我的练习'
    }
  },
  methods: {
    async fetchQuestionDetail(questionId) {
      const id = Number(questionId)
      if (!Number.isFinite(id)) return
      if (this.questionDetailCache[id]) return

      try {
        const res = await getPaperQuestion(id)
        if (!(res && res.code === 200 && res.data)) return

        const data = res.data
        this.questionDetailCache[id] = true

        const idx = this.questions.findIndex(q => Number(q.id) === id)
        if (idx < 0) return

        const prev = this.questions[idx]
        const type = this.mapQuestionType(data.questionType)
        const options = (data.options || []).map(opt => ({
          id: opt.id,
          letter: opt.label,
          content: opt.content,
          isCorrect: opt.isCorrect === true || opt.isCorrect === 1
        }))
        const correctAnswer = options.filter(o => o.isCorrect).map(o => o.id)
        const keywords = type === 'shortAnswer' ? options.filter(o => o.isCorrect).map(o => o.content).filter(Boolean) : []

        this.$set(this.questions, idx, {
          ...prev,
          type,
          title: data.questionContent || prev.title,
          analysis: data.analysis,
          options,
          correctAnswer,
          keywords
        })
      } catch (e) {
        this.$message.error('获取题目详情失败')
      }
    },
    mapQuestionType(type) {
      switch (Number(type)) {
        case 1:
          return 'single'
        case 2:
          return 'multiple'
        case 3:
          return 'single'
        case 4:
          return 'shortAnswer'
        default:
          return 'single'
      }
    },
    handlePaperData(data) {
      this.paperId = data.paperId
      this.paperSessionId = data.paperSessionId
      this.paperType = data.paperType
      this.examTitle = data.paperName || '练习试卷'
      this.totalScore = Number(data.totalScore) || 0
      this.passScore = Number(data.passScore) || 0
      this.startTime = Date.now()
      this.answers = {}
      this.confirmedQuestions = []

      this.questions = (data.questions || []).map(q => {
        const type = this.mapQuestionType(q.questionType)
        const options = (q.options || []).map(opt => ({
          id: opt.id,
          letter: opt.label,
          content: opt.content,
          isCorrect: opt.isCorrect === true || opt.isCorrect === 1
        }))
        const correctAnswer = options.filter(o => o.isCorrect).map(o => o.id)
        const keywords = type === 'shortAnswer' ? options.filter(o => o.isCorrect).map(o => o.content).filter(Boolean) : []

        return {
          id: q.id,
          type,
          title: q.questionContent,
          options,
          analysis: q.analysis,
          correctAnswer,
          keywords
        }
      })
    },
    async fetchPaper() {
      // 优先使用路由传递的参数（如错题练习生成时直接传递的数据）
      if (this.$route.params.paperData) {
        this.handlePaperData(this.$route.params.paperData)
        return
      }

      try {
        const res = await getPaperPractice(this.paperId)
        if (res && res.code === 200 && res.data) {
          this.handlePaperData(res.data)
        } else {
          this.$message.error((res && (res.message || res.msg)) || '获取试卷失败')
          setTimeout(() => this.$router.back(), 1500)
        }
      } catch (err) {
        this.$message.error((err && err.message) || '获取试卷失败')
        setTimeout(() => this.$router.back(), 1500)
      }
    },
    scrollToQuestion(index) {
      const el = this.$refs['question_' + index]
      if (el && el[0]) {
        el[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    selectOption(questionId, optionId, type) {
      if (type === 'single') {
        this.$set(this.answers, questionId, optionId)
        this.fetchQuestionDetail(questionId)
      } else {
        const current = Array.isArray(this.answers[questionId]) ? this.answers[questionId] : []
        const i = current.indexOf(optionId)
        if (i > -1) {
          current.splice(i, 1)
        } else {
          current.push(optionId)
        }
        this.$set(this.answers, questionId, current)
      }
    },
    // 提交答案并查看结果
    async submitAnswers() {
      const duration = Math.floor((Date.now() - this.startTime) / 1000)
      
      // 错题练习提交逻辑
      if (this.isWrongPractice) {
        try {
          const payload = {
            answers: this.answers,
            duration: duration > 0 ? duration : 0,
            startTime: this.startTime
          }
          const res = await submitWrongQuestionPractice(payload)
          const data = res.data || {}
          
          this.resultData = {
            source: 'wrong_practice',
            actualScore: data.correctCount, // 这里用正确数代替分数展示
            totalScore: data.totalCount, // 这里用总题数代替总分展示
            isPassed: data.accuracyRate >= 60, // 假设60%及格，或者不显示及格
            correctCount: data.correctCount,
            total: data.totalCount,
            duration: duration > 0 ? duration : 0,
            wrong: data.wrongCount,
            unanswered: data.totalCount - data.correctCount - data.wrongCount,
            accuracyRate: data.accuracyRate,
            questionScores: data.questionScores
          }
          this.showResultDialog = true
        } catch (err) {
          this.$message.error(err.message || '提交失败')
        }
        return
      }

      // 原有练习提交逻辑
      const formattedAnswers = {}
      this.questions.forEach(q => {
        const ans = this.answers[q.id]
        if (!ans || (Array.isArray(ans) && ans.length === 0)) return
        formattedAnswers[q.id] = ans
      })

      const payload = {
        answers: formattedAnswers,
        duration: duration > 0 ? duration : 0,
        paperId: Number(this.paperId),
        paperSessionId: this.paperSessionId,
        startTime: this.startTime
      }

      let correctCount = 0
      let unansweredCount = 0
      let wrongCount = 0

      const results = this.questions.map(q => {
        const ua = this.answers[q.id]
        const isUnanswered = !ua || (Array.isArray(ua) && ua.length === 0) || (q.type === 'shortAnswer' && !String(ua || '').trim())
        if (isUnanswered) {
          unansweredCount += 1
        }

        const correctOptions = (q.options || []).filter(o => o.isCorrect === true)
        const correctIds = correctOptions.map(o => o.id)

        let isCorrect = false
        if (!isUnanswered) {
          if (q.type === 'single') {
            isCorrect = correctIds.length === 1 && Number(ua) === Number(correctIds[0])
          } else if (q.type === 'multiple') {
            const userIds = Array.isArray(ua) ? ua.map(Number) : []
            isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.map(Number).includes(id))
          } else {
            isCorrect = this.isAnswerCorrect(q.id)
          }
        }

        if (isCorrect) {
          correctCount += 1
        } else if (!isUnanswered) {
          wrongCount += 1
        }

        const userAnswer =
          q.type === 'single'
            ? ((q.options || []).find(o => Number(o.id) === Number(ua)) || {}).letter || ''
            : q.type === 'multiple'
                ? (q.options || [])
                    .filter(o => Array.isArray(ua) && ua.map(Number).includes(Number(o.id)))
                    .map(o => o.letter)
                    .join(',')
                : String(ua || '').trim()

        const correctAnswer =
          q.type === 'shortAnswer'
            ? (Array.isArray(q.keywords) ? q.keywords.join('、') : '')
            : correctOptions.map(o => o.letter).join(',')

        return {
          id: q.id,
          content: q.title,
          isCorrect,
          userAnswer,
          correctAnswer
        }
      })

      try {
        const res = await submitFrontendExam(payload)
        if (res && res.code !== 200) {
          this.$message.error(res.message || '提交失败')
          return
        }
      } catch (err) {
        this.$message.error((err && err.message) || '提交失败')
        return
      }

      const total = this.questions.length
      const correctRate = total ? Math.round((correctCount / total) * 100) : 0

      this.$router.push({
        name: 'examSubmitResult',
        query: {
          source: 'practice',
          correctCount,
          correctRate,
          total,
          wrong: wrongCount,
          unanswered: unansweredCount,
          duration: duration > 0 ? duration : 0,
          practiceId: this.$route.params.id,
          results: JSON.stringify(results),
          returnPath: this.$route.query.returnPath
        }
      })
    },
    // 确认选项（多选题）
    confirmOption(questionId) {
      this.fetchQuestionDetail(questionId)
      if (!this.confirmedQuestions.includes(questionId)) {
        this.confirmedQuestions.push(questionId)
      }
    },
    // 确认提交（问答题）
    confirmShortAnswer(questionId) {
      this.fetchQuestionDetail(questionId)
      if (!this.confirmedQuestions.includes(questionId)) {
        this.confirmedQuestions.push(questionId)
      }
    },
    getQuestionStatus(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      if (!question) return 'unanswered'
      const answer = this.answers[questionId]
      if (question.type === 'shortAnswer') {
        if (!answer || !String(answer).trim()) return 'unanswered'
        if (!this.confirmedQuestions.includes(questionId)) return 'answered'
        const text = String(answer).toLowerCase()
        const keywords = Array.isArray(question.keywords) ? question.keywords : []
        const min = question.minKeywords || Math.max(1, Math.ceil(keywords.length / 2))
        const hit = keywords.filter(k => text.includes(String(k).toLowerCase())).length
        return hit >= min ? 'correct' : 'wrong'
      }
      if (!answer || (Array.isArray(answer) && answer.length === 0)) {
        return 'unanswered'
      }
      if (question.type === 'single') {
        const singleAnswer = Number(answer)
        return (question.correctAnswer || []).map(Number).includes(singleAnswer) ? 'correct' : 'wrong'
      } else {
        if (!this.confirmedQuestions.includes(questionId)) {
          return 'answered'
        }
        if (!Array.isArray(answer) || !Array.isArray(question.correctAnswer)) {
          return 'wrong'
        }
        const correct = [...question.correctAnswer].map(Number).sort((a, b) => a - b)
        const userAnswer = [...answer].map(Number).sort((a, b) => a - b)
        if (correct.length !== userAnswer.length) return 'wrong'
        return correct.every((val, index) => val === userAnswer[index]) ? 'correct' : 'wrong'
      }
    },
    isAnswerCorrect(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      if (!question) return false
      const answer = this.answers[questionId]
      if (question.type === 'shortAnswer') {
        if (!answer || !String(answer).trim()) return false
        const text = String(answer).toLowerCase()
        const keywords = Array.isArray(question.keywords) ? question.keywords : []
        const min = question.minKeywords || Math.max(1, Math.ceil(keywords.length / 2))
        const hit = keywords.filter(k => text.includes(String(k).toLowerCase())).length
        return hit >= min
      }
      if (!answer || (Array.isArray(answer) && answer.length === 0)) {
        return false
      }
      if (question.type === 'single') {
        const singleAnswer = Number(answer)
        return (question.correctAnswer || []).map(Number).includes(singleAnswer)
      } else {
        if (!Array.isArray(answer) || !Array.isArray(question.correctAnswer)) {
          return false
        }
        const correct = [...question.correctAnswer].map(Number).sort((a, b) => a - b)
        const userAnswer = [...answer].map(Number).sort((a, b) => a - b)
        if (correct.length !== userAnswer.length) return false
        return correct.every((val, index) => val === userAnswer[index])
      }
    },
    getCorrectAnswerLetters(question) {
      return question.correctAnswer.map(id => {
        const option = question.options.find(opt => opt.id === id)
        return option ? option.letter : ''
      }).join(', ')
    },
    getUserAnswerLetters(questionId) {
      const question = this.questions.find(q => q.id === questionId)
      if (!question) return ''
      const answer = this.answers[questionId]
      if (!answer || (Array.isArray(answer) && answer.length === 0)) return ''
      if (question.type === 'single') {
        const opt = question.options.find(opt => opt.id === answer)
        return opt ? opt.letter : ''
      } else {
        const arr = Array.isArray(answer) ? answer : []
        return arr.map(id => {
          const opt = question.options.find(opt => opt.id === id)
          return opt ? opt.letter : ''
        }).filter(Boolean).join(', ')
      }
    },
    // 计算正确率
    calculateCorrectRate() {
      if (this.questions.length === 0) return 0
      
      let correctCount = 0
      this.questions.forEach(question => {
        if (this.isAnswerCorrect(question.id)) {
          correctCount++
        }
      })
      
      return Math.round((correctCount / this.questions.length) * 100)
    },
    // 计算错题数
    calculateWrongCount() {
      let wrongCount = 0
      this.questions.forEach(question => {
        const status = this.getQuestionStatus(question.id)
        if (status === 'wrong') {
          wrongCount++
        }
      })
      return wrongCount
    },
    // 计算未答数
    calculateUnansweredCount() {
      let unansweredCount = 0
      this.questions.forEach(question => {
        const status = this.getQuestionStatus(question.id)
        if (status === 'unanswered') {
          unansweredCount++
        }
      })
      return unansweredCount
    },
    // 检查是否所有题目都已回答
    isAllAnswered() {
      return this.questions.every(question => {
        const answer = this.answers[question.id]
        return answer && (Array.isArray(answer) ? answer.length > 0 : true)
      })
    },
    // 再次答题
    retryPractice() {
      this.answers = {}
      this.showResultDialog = false
      this.fetchPaper()
    },
    // 返回我的练习
    backToMyCourses() {
      const source = this.$route.query.source
      if (source === 'wrong_practice') {
        this.$router.push({ name: 'memberErrorNotes' })
      } else {
        this.$router.push({ name: 'memberSimulation' })
      }
    },
    // 退出练习，返回上一个页面
    exitPractice() {
      const source = this.$route.query.source
      if (source === 'wrong_practice') {
        this.$router.push({ name: 'memberErrorNotes' })
      } else {
        this.$router.push({ name: 'memberSimulation' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.practice-detail {
  .exam-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 40px;
    border-bottom: 1px solid #e8e8e8;
    
    .logo {
      display: flex;
      align-items: center;
      
      .logo-img {
        height: 72px;
        width: auto;
      }
    }
    
    .exam-title {
      font-size: 18px;
    }
    
    .exit-exam {
      color: #3579f7;
      cursor: pointer;
    }
  }
  
  .exam-nav {
    padding: 10px 40px;
    background-color: #f5f7fa;
  }
  
  .exam-content {
    display: flex;
    padding: 20px 40px;
  }
  
  .left-panel {
    width: 300px;
    margin-right: 30px;
  }
  
  .answer-card {
    background-color: #fff;
    padding: 25px;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    
    .card-title {
      text-align: center;
      margin-bottom: 25px;
      font-weight: 500;
      font-size: 18px;
      color: #333;
    }
    
    .question-numbers {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 15px;
      margin-bottom: 30px;
      justify-items: center;
    }
    
    .question-number {
      width: 36px;
      height: 36px;
      background-color: #f5f5f5;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 14px;
      color: #666;
      transition: all 0.2s;
    }
    
    .question-number:hover {
      border-color: #3E2CCD;
      color: #3E2CCD;
    }
    
    .question-number.correct,
    .question-number.wrong {
      background-color: #ECEBFF;
      border-color: #3E2CCD;
      color: #3E2CCD;
    }
    
    .question-number.unanswered {
      background-color: #f5f5f5;
      border-color: #e0e0e0;
      color: #666;
    }
    
    .divider {
      height: 1px;
      border-top: 1px dashed #e0e0e0;
      margin-bottom: 20px;
      width: 100%;
    }
    
    .card-footer {
      .status-info {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        font-size: 14px;
        color: #666;
        gap: 20px;

        .status-item {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .status-box {
          width: 16px;
          height: 16px;
          border-radius: 2px;

          &.answered {
            background-color: #ECEBFF;
            border: 1px solid #3E2CCD;
          }

          &.unanswered {
            background-color: #f5f5f5;
            border: 1px solid #e0e0e0;
          }
        }
      }
      
      .submit-btn {
        width: 100%;
        background-color: #3E2CCD;
        border-color: #3E2CCD;
        padding: 12px 0;
        font-size: 16px;
        border-radius: 4px;
        height: 44px;
      }
      
      .submit-btn:hover {
        background-color: #3325ad;
        border-color: #3325ad;
      }
    }
  }
  
  .right-panel {
    flex: 1;
    background-color: white;
    padding: 20px;
    border-radius: 5px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }
  
  .question-item {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e8e8e8;
    
    .question-header {
      display: flex;
      margin-bottom: 20px;
      align-items: center;
      
      .question-number {
        font-weight: bold;
        margin-right: 10px;
        color: #722ed1;
        font-size: 18px;
        width: auto;
        height: auto;
        background: none;
        border: none;
        border-radius: 0;
      }
      
      .question-type {
        margin-left: 8px;
        padding: 2px 8px;
        background-color: #f7f1ff;
        color: #722ed1;
        border-radius: 4px;
        font-size: 12px;
        border: 1px solid #e6d9ff;
      }
    }
    
    .question-options {
      margin-left: 30px;
    }
    
    .option-item {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      cursor: pointer;
      
      &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
    
    .option-circle {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 1px solid #d9d9d9;
      background-color: #fff;
      color: #333;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover {
        border-color: #722ed1;
      }
    }
    
    .option-item.selected .option-circle {
      background-color: #722ed1;
      color: white;
      border-color: #722ed1;
    }
    
    .option-text {
      font-size: 15px;
      color: #333;
      line-height: 1.6;
    }
    
    .option-item.selected .option-text {
      color: #722ed1;
      font-weight: 500;
    }
    
    .short-answer {
      margin-left: 30px;
      margin-bottom: 12px;
      
      .el-textarea__inner {
        min-height: 140px;
        border-radius: 8px;
      }
    }
    
    .confirm-option {
      margin-left: 30px;
      margin-top: 16px;
      text-align: left;
      
      .el-button {
        background-color: #f5f7fa;
        border-color: #e8e8e8;
        color: #606266;
      }
      
      .el-button:hover {
        background-color: #ebedf0;
        border-color: #dcdfe6;
      }
      
      .el-button.is-disabled {
        background-color: #f5f7fa;
        border-color: #e8e8e8;
        color: #c0c4cc;
        cursor: not-allowed;
      }
    }
    
    .question-analysis {
      margin-top: 20px;
      padding: 16px;
      border-radius: 4px;
      margin-left: 30px;
      
      &.correct {
        background-color: #f6ffed;
        border: 1px solid #b7eb8f;
      }
      
      &.wrong {
        background-color: #fff1f0;
        border: 1px solid #ffccc7;
      }
      
      .analysis-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        
        .analysis-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          
          &.correct {
            color: #52c41a;
            border: 1px solid #b7eb8f;
            background-color: #f6ffed;
          }
          
          &.wrong {
            color: #ff4d4f;
            border: 1px solid #ffccc7;
            background-color: #fff1f0;
          }
        }
        
        .analysis-title {
          font-weight: 600;
          font-size: 15px;
          
          &.correct { color: #52c41a; }
          &.wrong { color: #ff4d4f; }
        }
      }
      
      .analysis-row {
        margin-bottom: 8px;
        
        .label { color: #595959; margin-right: 4px; }
        .value { color: #333; }
        .value.correct { color: #52c41a; }
        .value.user { color: #722ed1; }
      }
    }
  }
  

  
  /* 结果弹窗样式 */
  .result-content {
    .result-score {
      display: flex;
      justify-content: center;
      margin-bottom: 30px;
      
      .score-circle {
        width: 150px;
        height: 150px;
        border-radius: 50%;
        background-color: #722ed1;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        overflow: hidden;
        
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #722ed1 0%, #b35ffb 100%);
        }
        
        .score-value {
          position: relative;
          z-index: 1;
          color: white;
          font-size: 36px;
          font-weight: bold;
        }
      }
    }
    
    .result-stats {
      text-align: center;
      
      .stat-item {
        margin-bottom: 12px;
        font-size: 16px;
        
        .stat-label {
          color: #606266;
          margin-right: 8px;
        }
        
        .stat-value {
          color: #722ed1;
          font-weight: bold;
        }
      }
    }
  }
}
</style>
